import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ShieldCheck, Zap, FileCheck, Clock, ChevronRight, Phone, MapPin } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9c5cf9e4-7ffa-44c2-89bf-7aacf8535caa/artifacts/4q3hto4h_634037357_1389521753189000_3144761508300406294_n.jpg';

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleCTA = () => navigate(isAuthenticated ? '/dashboard' : '/login');

  return (
    <div className="min-h-screen font-sans bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* NAV */}
      <nav className="fixed top-0 w-full z-50 bg-[#1a3a5c]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Cuerpo de Rescate" className="h-9 w-9 rounded-full object-cover" />
            <span className="text-white font-black text-lg tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              FRAP <span className="text-[#4ade80]">Digital</span>
            </span>
          </div>
          <button
            onClick={handleCTA}
            className="bg-[#1a7a4a] hover:bg-[#145d38] text-white text-sm font-bold px-4 py-2 rounded-md transition-colors uppercase tracking-wide"
            data-testid="nav-crear-frap-btn"
          >
            {isAuthenticated ? 'Mi Panel' : 'Iniciar Sesión'}
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="min-h-screen flex items-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d1f35 0%, #1a3a5c 50%, #0d2818 100%)' }}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,.1) 40px, rgba(255,255,255,.1) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,.1) 40px, rgba(255,255,255,.1) 41px)' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-16 relative">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-green-300 text-xs font-semibold uppercase tracking-widest">Sistema Oficial · Cuerpo de Rescate Ensenada</span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-4 leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                FRAP<br /><span className="text-[#4ade80]">Digital</span>
              </h1>
              <p className="text-slate-300 text-lg sm:text-xl max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">
                Formulario de Rescate y Atención Prehospitalaria digital. Rápido, preciso y con generación de PDF oficial al instante.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={handleCTA}
                  className="flex items-center justify-center gap-2 bg-[#1a7a4a] hover:bg-[#16a360] text-white font-black text-base px-8 py-4 rounded-md transition-all shadow-lg shadow-green-900/30 uppercase tracking-wide active:scale-95"
                  data-testid="hero-crear-frap-btn"
                >
                  Crear FRAP <ChevronRight className="w-5 h-5" />
                </button>
                <a href="#como-funciona" className="flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white/60 text-white font-bold text-base px-8 py-4 rounded-md transition-all uppercase tracking-wide">
                  Cómo Funciona
                </a>
              </div>
            </div>
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-72 h-72 rounded-full bg-[#1a7a4a]/20 border-2 border-[#1a7a4a]/40 flex items-center justify-center shadow-2xl">
                  <img src={LOGO_URL} alt="Cuerpo de Rescate de Ensenada" className="w-56 h-56 rounded-full object-cover shadow-xl" />
                </div>
                <div className="absolute -top-4 -right-4 bg-[#1a7a4a] text-white rounded-full p-3 shadow-lg">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-[#1a3a5c] text-white rounded-full p-3 shadow-lg border border-white/20">
                  <FileCheck className="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-black text-[#1a3a5c] mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Herramientas del Sistema
            </h2>
            <p className="text-slate-500 text-base max-w-xl mx-auto">
              Todo lo necesario para documentar atenciones prehospitalarias de forma digital y oficial.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Zap className="w-7 h-7" />, title: 'Registro de Pacientes', desc: 'Captura completa de datos del paciente, atención y traslado.' },
              { icon: <FileCheck className="w-7 h-7" />, title: 'Generación de PDF', desc: 'PDF oficial idéntico al formato FRAP físico del Cuerpo de Rescate.' },
              { icon: <ShieldCheck className="w-7 h-7" />, title: 'Captura de Firmas', desc: 'Firmas digitales del paciente y paramédico con validez legal.' },
              { icon: <Clock className="w-7 h-7" />, title: 'Folio Automático', desc: 'Número de folio asignado automáticamente. Sin duplicados.' },
            ].map((b, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-6 hover:border-[#1a7a4a] hover:shadow-md transition-all group">
                <div className="w-12 h-12 bg-[#1a7a4a]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#1a7a4a] transition-colors">
                  <span className="text-[#1a7a4a] group-hover:text-white transition-colors">{b.icon}</span>
                </div>
                <h3 className="font-black text-[#1a3a5c] text-base mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{b.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section id="como-funciona" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-black text-[#1a3a5c] mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Cómo Funciona
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { num: '01', title: 'Ingresa a tu Unidad', desc: 'Accede con las credenciales de tu unidad o perfil de administrador.' },
              { num: '02', title: 'Llena el FRAP', desc: 'Completa todos los campos del formato. El sistema asigna folio automáticamente.' },
              { num: '03', title: 'Descarga el PDF', desc: 'Genera el PDF oficial con firmas digitales, listo para entregar o archivar.' },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-[#1a7a4a] rounded-full flex items-center justify-center mb-4 shadow-lg">
                  <span className="text-white text-xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.num}</span>
                </div>
                <h3 className="font-black text-[#1a3a5c] text-base mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATAFORMA DIGITAL FRAP */}
      <section className="py-20 bg-[#1a3a5c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1">
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Plataforma Digital FRAP
              </h2>
              <p className="text-slate-300 text-base leading-relaxed mb-6">
                Sistema diseñado para el registro digital de atenciones prehospitalarias y la generación del formato FRAP oficial del Cuerpo de Rescate de Ensenada, A.C.
              </p>
              <ul className="space-y-3">
                {['Registro de pacientes', 'Captura de firmas', 'Generación de PDF', 'Folio automático', 'Archivo institucional'].map(item => (
                  <li key={item} className="flex items-center gap-3 text-slate-200 text-sm">
                    <div className="w-5 h-5 bg-[#1a7a4a] rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0">
              <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
                <img src={LOGO_URL} alt="Logo" className="w-32 h-32 rounded-full mx-auto mb-4 shadow-xl" />
                <p className="text-white font-black text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>Cuerpo de Rescate</p>
                <p className="text-[#4ade80] font-semibold text-sm">de Ensenada, A.C.</p>
                <p className="text-slate-400 text-xs mt-1">Fundado en 1978</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 bg-white text-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <h2 className="text-4xl font-black text-[#1a3a5c] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Listo para Usar
          </h2>
          <p className="text-slate-500 text-base mb-8">
            Accede con tu unidad y comienza a generar FRAPs digitales de inmediato.
          </p>
          <button
            onClick={handleCTA}
            className="inline-flex items-center gap-2 bg-[#1a7a4a] hover:bg-[#145d38] text-white font-black text-lg px-10 py-4 rounded-md transition-all shadow-lg shadow-green-900/20 uppercase tracking-wide active:scale-95"
            data-testid="cta-final-btn"
          >
            Crear FRAP <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0d1f35] py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-full" />
            <div>
              <p className="text-white text-sm font-bold">FRAP Digital</p>
              <p className="text-slate-500 text-xs">Cuerpo de Rescate de Ensenada, A.C.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4 text-slate-500 text-xs">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Calle Magnolias No. 2356, Ensenada, B.C.</span>
            <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> 646 152 4659</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
